import * as React from 'react';

import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize } from 'lodash';

import { Formatter as CategoryFormatter } from '@admin/components/categories/formatter';
import { Labels as DimensionsLabels } from '@admin/components/dimensions/labels';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { IItem } from '@admin/types/item';
import { incidentKindDisplayLabel } from '@admin/utils/incident_kind_labels';
import { normalizeItemToGraphQL } from '@admin/utils/item_normalization';

import { Hero } from './hero';
import { ItemCategoryEditor } from './item_category_editor';
import { MaterialCategoryPreview } from './material_category_preview';
import { Metadata } from './metadata';
import { PartCategoryPreview } from './part_category_preview';

export interface ItemBrickProps {
  item: IItem;
  sizeable?: boolean;
  categoryBulkEditMode?: boolean;
  onItemUpdate?: (updatedItem: IItem) => void;
}

export const ItemBrick: React.FC<ItemBrickProps> = ({
  item: rawItem,
  sizeable = false,
  categoryBulkEditMode,
  onItemUpdate,
}) => {
  // This can be removed when parent Angular components are migrated to React and prop changes are handled by parent
  const [currentItem, setCurrentItem] = React.useState(rawItem);

  // Update local state when prop changes (for Angular parent updates)
  React.useEffect(() => {
    setCurrentItem(rawItem);
  }, [rawItem]);

  // Normalize Angular fields to GraphQL format (camelCase)
  const item = normalizeItemToGraphQL(currentItem);

  // Handle item updates from the category editor
  const handleItemUpdate = React.useCallback(
    (updatedItem: IItem) => {
      // Update local state for immediate UI refresh (Angular bridge)
      setCurrentItem(updatedItem);

      // Call parent callback if provided (for React parents)
      if (onItemUpdate) {
        onItemUpdate(updatedItem);
      }
    },
    [onItemUpdate],
  );

  return (
    <div className="panel brick">
      {item.latestOpenIncidentKind && (
        <span className="brick-badge brick-badge-danger">
          {incidentKindDisplayLabel(item.latestOpenIncidentKind) ?? capitalize(item.latestOpenIncidentKind)}
        </span>
      )}

      <a className="brick-label" href={`/accounts/${item.accountID}/items/${item.id}`}>
        <figure className="brick-square">
          <Hero selection={item} width={480} height={480} className="brick-image" />
        </figure>
      </a>

      <div className="brick-metadata">
        <a href={`/accounts/${item.accountID}/items/${item.id}`}>
          <Metadata item={item} />
        </a>

        {item.group && (
          <div>
            <FontAwesomeIcon icon={faLink} />
            <span>to</span>
            <span>
              <CategoryFormatter resource={item.group} fallback="No Category" />
            </span>
          </div>
        )}

        {item.packaging && (
          <span>
            <span>as</span>
            <MaterialCategoryPreview item={item} />
            <PartCategoryPreview item={item} />
          </span>
        )}

        <QaQrCodeLink value={item.barcode.value} />
        <br />

        <div className="brick-category-editor">
          <ItemCategoryEditor
            item={item}
            onSave={handleItemUpdate}
            readOnly={
              !!item.group ||
              !!item.packaging ||
              !sizeable ||
              (categoryBulkEditMode !== undefined && !categoryBulkEditMode)
            }
          />
        </div>
      </div>
    </div>
  );
};
